<script lang="ts">
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import ProductCardBadge from "@shared/svelte/Components/ProductLines/ProductCardBadge.svelte";
  import ProductCardDistributors from "@shared/svelte/Components/ProductLines/ProductCardDistributors.svelte";
  import ProductCardPrices from "@shared/svelte/Components/ProductLines/ProductCardPrices.svelte";
  import ProductCardSalesDivisions from "@shared/svelte/Components/ProductLines/ProductCardSalesDivisions.svelte";
  import ProductCardVintages from "@shared/svelte/Components/ProductLines/ProductCardVintages.svelte";
  import SkuItems from "@shared/svelte/Components/ProductLines/ProductListCard/SkuItems.svelte";
  import {
    ProductLineListingStore,
    updateProductLineListingStore,
  } from "@retailer/svelte/ProductLines/ProductLineContainer/stores/ProductLineListingStore";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import Skeleton from "svelte-skeleton/Skeleton.svelte";
  import TrackingContext from "@shared/svelte/Components/Tracking/TrackingContext.svelte";
  import { compact, includes, uniq } from "lodash";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import { pluralize } from "@shared/svelte/Modules/pluralize";
  import { goto } from "@roxi/routify";
  import { isRestrictedRole } from "@retailer/services/preview_forbidden_flash";
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import {
    buildProductCardTrackingEntities,
    canShowProductCardBadges,
    filteredVerifiedItemsInStockCount,
    showProductLineDealIcon,
    showFeaturedLabel,
    getBadgeText,
    getIconType,
    getTextColor,
    getProductLineStockInfo,
    getDualProductStockInfo,
  } from "@retailer/services/product_card_service";
  import RetailerFulfillmentMethodsStore from "@retailer/svelte/stores/RetailerFulfillmentMethodsStore";
  import { EventTracking } from "@shared/services/event_tracking/events";
  import InventoryStatusStore from "@shared/svelte/Stores/InventoryStatusStore";
  import { getDataForInventories } from "@retailer/services/pricing_data_service";
  import RetailerUserAuthenticationsStore from "@retailer/svelte/stores/RetailerUserAuthenticationsStore";
  import { shouldHideAllDistributorInfo } from "@shared/services/retailer_user_authentication_service";

  import type { DualProductDistributor } from "@shared/types/Distributor";
  import type { ProductCardPricesEntity } from "@shared/services/event_tracking/entities";
  import type { PricingDataResult } from "@shared/types/Pricing";
  import {
    Inventory,
    ProductLine,
    InventoryPrice,
    ProductListAccordionState,
    ProductCardInventoryStatus,
  } from "@shared/types/ProductLine";
  import { onDestroy } from "svelte";
  import DiscountsLookupStore from "@retailer/svelte/stores/DiscountsLookupStore";
  import type { DiscountResult } from "@retailer/services/discounts_service";
  import type { RetailerFulfillmentMethod } from "@shared/types/RetailerFulfillmentMethod";

  const { trackMrcEvents, trackClick } = EventTracking;

  export let productLine: ProductLine;
  export let Cart = null;
  export let stateParams: any;
  export let index: number;
  export let hidePrice: boolean = false;
  export let distributorOverride: { id: number; name: string } = null;

  const vintages: number[] = uniq(compact(productLine.products?.map(p => p.vintage_year)))
    .sort()
    .reverse();
  const displayVintages = vintages?.length > 0;

  let productInventory: Inventory[] = productLine.products?.map(p => p.inventory)?.flat() || [];
  let productInventoryIds: number[] = productInventory.map(i => i.id);
  let productInventoryCount: number = productInventory?.length || 0;
  let inventoryPrices: InventoryPrice[];
  let loadingPrices: boolean = true;
  let productLineDualDistributors: DualProductDistributor[] =
    productLine.dual_product_distributors || [];
  let accordionOpen: boolean = false;
  let inventoryStatuses: ProductCardInventoryStatus[] = [];
  let itemsInStockCount: number = 0;
  let showItemsInStock: boolean = false;
  let showFuturePricesAvailable: boolean = false;
  let showBadges: boolean = false;

  let productDataFetched: boolean = false;
  let discountDataFetched: boolean = false;
  let futurePriceDataFetched: boolean = false;
  let listViewAccordionStatesKey: string | number = productLine?.is_paid
    ? `${productLine.id}-ad`
    : productLine.id;

  const distributorInfoAllowsBadges: boolean = canShowProductCardBadges(
    productLine.distributor_info?.distributor_id,
    productLineDualDistributors,
  );

  const unsubscribeInventoryStatus = InventoryStatusStore.subscribe(n => {
    inventoryStatuses = n.inventoryStatuses[productLine.id] || [];
  });

  const overrideDistributorId = stateParams => {
    return stateParams.distributors?.length ? parseInt(stateParams.distributors[0]) : null;
  };

  const wineCellarStagedRolloutEnabled = stagedRolloutCheck(
    "marketplace__wc_for_geolocation_and_account_based_distributors",
  );
  const canUseWineCellarStockInfo = () => {
    let canUseWineCellar = false;
    if (productLine?.distributor_info?.distributor_id) {
      canUseWineCellar = productLine.distributor_info.uses_wine_cellar;
    } else if (productLine?.dual_product_distributors) {
      const distributorId = overrideDistributorId(stateParams) || distributorOverride?.id;
      if (distributorId) {
        canUseWineCellar = productLine.dual_product_distributors.find(
          d => d.id === distributorId,
        )?.uses_wine_cellar;
      } else {
        canUseWineCellar = productLine.dual_product_distributors.every(d => d.uses_wine_cellar);
      }
    }
    return wineCellarStagedRolloutEnabled && canUseWineCellar;
  };
  const unsubscribeListingStore = ProductLineListingStore.subscribe(n => {
    inventoryPrices = n.inventoryPrices;
    loadingPrices = n.loadingPrices;
    accordionOpen =
      n.listViewAccordionStates &&
      !!n.listViewAccordionStates[listViewAccordionStatesKey] &&
      productInventoryCount > 0;

    showFuturePricesAvailable = productLine.future_prices_available;

    if (!canUseWineCellarStockInfo()) {
      itemsInStockCount = filteredVerifiedItemsInStockCount(
        inventoryStatuses,
        productLine.products?.map(p => p.inventory)?.flat(),
      );
      showItemsInStock = itemsInStockCount > 0;
      showBadges = (showItemsInStock || showFuturePricesAvailable) && distributorInfoAllowsBadges;
    }
    futurePriceDataFetched = includes(
      Object.keys(n.productLineFuturePrices || {}),
      productLine.id.toString(),
    );
  });

  onDestroy(() => {
    unsubscribeInventoryStatus();
    unsubscribeListingStore();
  });

  const isInAdminUI = window.location.pathname.startsWith("/admin");

  $: mobileMode = $LayoutStore.isMobile;

  const hasDeal = showProductLineDealIcon(productLine);

  let productCardPricesEntity: ProductCardPricesEntity;
  const trackingParams = (stockStatus: string | undefined) =>
    buildProductCardTrackingEntities(
      productLine,
      hasDeal,
      index,
      "list",
      // undefined props get removed from the entity altogether
      inventoryStatuses ? distributorInfoAllowsBadges && showItemsInStock : undefined,
      stockStatus || undefined,
      distributorInfoAllowsBadges ? showFuturePricesAvailable : undefined,
      displayVintages,
      true, // List view cards can always display sales divisions
    );
  const productCardTargetObjectType = "product_card";

  const formatGeography = productLine => {
    const wineRegionName = productLine.wine_region_name;
    const wineCountryName = productLine.wine_country_name;
    if (wineRegionName && wineCountryName) {
      return `${wineRegionName}, ${wineCountryName}`;
    } else if (wineRegionName) {
      return wineRegionName;
    } else if (wineCountryName) {
      return wineCountryName;
    } else {
      return;
    }
  };

  const formatCategoryInfo = () => {
    const category = productLine.category_name;
    const subcategoryName = productLine.subcategory_name;

    let result = "";
    if (category) {
      result += category;
    }
    if (category && subcategoryName) {
      result += " - ";
    }
    if (subcategoryName) {
      result += subcategoryName;
    }
    return result;
  };

  const getProductData = async () => {
    if (productDataFetched) return;

    // if on distributor PLP, we want to override the
    // the retailer distributor matching and just uses the
    // plp distributor
    const overrideDistributor = stateParams => {
      const hasDistributors = stateParams.distributors?.length ? true : null;
      const tier = productLine?.bestDistributorTier || 0;
      return hasDistributors || tier > 1 ? true : null;
    };
    const distributorId = productLine?.distributor_info?.distributor_id;

    const res = await HttpPromise({
      method: "GET",
      url: "/api/retailer/product_lines/" + productLine.id,
      data: {
        distributor_id: overrideDistributorId(stateParams) || distributorId,
        distributor_override: overrideDistributor(stateParams),
      },
    }).catch(() => []);

    updateProductLineListingStore(n => {
      n.productData[productLine.id] = res?.products;
      return n;
    });
    productDataFetched = true;
  };

  const getPricingData = async () => {
    if (discountDataFetched && futurePriceDataFetched) return;

    const dualProductDistributors = productLine?.dual_product_distributors || [];
    const stagedRolloutEnabled = stagedRolloutCheck("future_pricing");
    let discounts = [];
    let futurePricesResult = [];

    if (!!dualProductDistributors.length || productLine?.distributor_info?.distributor_id) {
      const inventoryIds = productInventoryIds.map(String);

      if (inventoryIds.length > 0) {
        try {
          const pricingData: PricingDataResult[] = await getDataForInventories(inventoryIds);
          discounts = pricingData.map(p => p.discounts).flat();
          DiscountsLookupStore.set(discounts as DiscountResult[]);
          if (stagedRolloutEnabled && !isRestrictedRole(["supplier"])) {
            futurePricesResult = pricingData.map(p => p.future_price).flat();
          }
        } catch {
          discounts = [];
          futurePricesResult = [];
        }
      }
    }

    updateProductLineListingStore(n => {
      n.discountData[productLine.id] = discounts;
      n.productLineFuturePrices[productLine.id] = futurePricesResult;
      return n;
    });
    discountDataFetched = true;
    futurePriceDataFetched = true;
  };

  let hasAccordion: boolean = false;
  let accordionIcon: string = "ArrowDown";
  $: {
    accordionIcon = !accordionOpen ? "ArrowDown" : "ArrowUp";

    let accordionDistroCondition: boolean;
    if (stagedRolloutCheck("dual_products")) {
      accordionDistroCondition =
        !!productLineDualDistributors?.length || !!productLine?.distributor_info?.distributor_id;
    } else {
      accordionDistroCondition =
        (productLineDualDistributors?.length ? productLineDualDistributors.length === 1 : true) &&
        !!productLine?.distributor_info?.distributor_id;
    }

    hasAccordion = accordionDistroCondition && productInventoryCount > 0 && !hideAllDistributorInfo;
  }

  const onClickToPdp = (
    targetObjectType = productCardTargetObjectType,
    trackingContextEntities,
  ) => {
    trackClick(targetObjectType, compact(trackingContextEntities.concat(productCardPricesEntity)));
    $goto(`/products/${productLine.id}`);
  };

  const storeAccordionState = accordionIsOpen => {
    updateProductLineListingStore(n => {
      if (accordionIsOpen) {
        n.listViewAccordionStates[listViewAccordionStatesKey] = ProductListAccordionState.Open;
      } else {
        delete n.listViewAccordionStates[listViewAccordionStatesKey];
      }
      return n;
    });
  };

  const onClickAccordion = trackingContextEntities => {
    if (hasAccordion) {
      if (accordionOpen) {
        trackClick("collapse_accordion", trackingContextEntities, false);
      } else {
        trackClick("expand_accordion", trackingContextEntities);
      }
      getProductData();
      getPricingData();
      accordionOpen = !accordionOpen;
      storeAccordionState(accordionOpen);
    }
  };

  const onClick = (event, trackingContextEntities) => {
    if (isInAdminUI) {
      return;
    }
    if (event.target.id === "product-card-name") {
      onClickToPdp("product_card_name", trackingContextEntities);
    } else if (event.target.id === "product-card-image") {
      onClickToPdp("product_card_image", trackingContextEntities);
    } else if (
      event.target.id === `product-list-clickable-card-${productLine.id}` ||
      event.target.id === "product-details-box"
    ) {
      onClickAccordion(trackingContextEntities);
    }
  };

  let accordionButtonTestId: string = "accordion-button-closed";
  $: {
    if (accordionOpen) {
      accordionButtonTestId = "accordion-button-open";
    } else {
      accordionButtonTestId = "accordion-button-closed";
    }
  }

  let showOrderingDisabledHeader: boolean = false;
  let orderingDisabledDistributorNames: string = "";
  $: {
    if (accordionOpen) {
      if (productLine.distributor_info?.ordering_disabled) {
        orderingDisabledDistributorNames = productLine.distributor_info.distributor_name;
        showOrderingDisabledHeader = true;
      } else if (productLineDualDistributors) {
        let disabledDistros = productLineDualDistributors.filter(
          distributor => distributor.ordering_disabled,
        );
        let lastDistro = disabledDistros.pop();
        if (disabledDistros.length > 1) {
          orderingDisabledDistributorNames = `${disabledDistros
            .map(distro => distro.name)
            .join(", ")}, or ${lastDistro.name}`;
          showOrderingDisabledHeader = true;
        } else if (disabledDistros.length == 1) {
          orderingDisabledDistributorNames = `${disabledDistros[0].name} or ${lastDistro.name}`;
          showOrderingDisabledHeader = true;
        } else if (lastDistro) {
          orderingDisabledDistributorNames = lastDistro.name;
          showOrderingDisabledHeader = true;
        }
      }
    } else {
      showOrderingDisabledHeader = false;
      orderingDisabledDistributorNames = "";
    }
  }

  let hideAllDistributorInfo: boolean = false;
  let productLineDistributorIds: number[];
  const hasRetailerUserAuthRollout = stagedRolloutCheck(
    "marketplace__retailer_user_authentication",
  );

  if (productLine?.distributor_info?.distributor_id) {
    productLineDistributorIds = [productLine?.distributor_info?.distributor_id];
  } else if (productLine.dual_product_distributors) {
    productLineDistributorIds = productLine.dual_product_distributors?.map(d => d.id);
  }

  $: hideAllDistributorInfo =
    hasRetailerUserAuthRollout &&
    shouldHideAllDistributorInfo($RetailerUserAuthenticationsStore, productLineDistributorIds || []);

  let fulfillmentMethod: RetailerFulfillmentMethod;
  $: {
    if (canUseWineCellarStockInfo() && productLine?.distributor_info?.distributor_id) {
      fulfillmentMethod =
        $RetailerFulfillmentMethodsStore[productLine.distributor_info.distributor_id] || null;
    }
  }

  const getProductLineStockStatus = async (fulfillmentMethod: RetailerFulfillmentMethod) => {
    if (!canUseWineCellarStockInfo()) return Promise.resolve(undefined);

    let stockStatus;
    const catalogDistributorId = overrideDistributorId(stateParams) || distributorOverride?.id;
    if (productLine.distributor_info.distributor_id) {
      stockStatus = await getProductLineStockInfo(
        productInventory,
        productLine.distributor_info.distributor_id,
        productLine.distributor_info.account_number,
        fulfillmentMethod,
        productLine.distributor_info.default_stock_site_id,
      );
    } else if (productLine.dual_product_distributors) {
      stockStatus = await getDualProductStockInfo(
        productInventory,
        productLine.dual_product_distributors,
        catalogDistributorId,
      );
    }
    showItemsInStock = stockStatus?.length > 0;
    showBadges = (showItemsInStock || showFuturePricesAvailable) && distributorInfoAllowsBadges;
    return stockStatus;
  };
</script>

<style>
  .click-border {
    border: 1px solid #008fbe;
  }

  .fizz-icon-button:hover {
    background: none;
  }

  .default-cursor {
    cursor: default;
  }
</style>

{#await getProductLineStockStatus(fulfillmentMethod) then productLineStockStatus}
  <TrackingContext entities={trackingParams(productLineStockStatus)} let:trackingContext>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="fizz-bordered fizz-bordered--hover"
      class:click-border={accordionOpen}
      data-testid={`product-list-card-${productLine.id}`}
      product-line-id={productLine.id}
      id={`product-list-card-${productLine.id}`}
      use:trackMrcEvents={[
        trackingContext.entities,
        { targetObjectType: productCardTargetObjectType },
      ]}
      on:click={e => onClick(e, trackingContext.entities)}
    >
      {#if showOrderingDisabledHeader}
        <div class="fizz-inset-square-8">
          <div class="fizz-alert-warning fizz-alert--small">
            Provi is temporarily not accepting orders for {orderingDisabledDistributorNames}, but
            you can still add items to your cart to order later. Contact your rep for more
            information.
          </div>
        </div>
      {/if}
      <div
        data-testid="product-list-clickable-card-{productLine.id}"
        id="product-list-clickable-card-{productLine.id}"
        class:cursor-pointer={hasAccordion}
        class:fizz-flex-row={!mobileMode}
        class:fizz-space-between={!mobileMode}
        class:fizz-flex-vertical={mobileMode}
      >
        <div class="fizz-inset-square-8">
          <figure class="fizz-flex-row-8" id="product-list-clickable-card-{productLine.id}">
            <a
              id="product-card-image"
              href={`/products/${productLine.id}`}
              data-testid="product-card-image"
              on:click|preventDefault
            >
              <ProductImage
                product={productLine}
                width={72}
                height={72}
                fizz={true}
                aspectRatio={"70"}
                usePlaceholderImage={true}
                className={"fizz-stack-8"}
                extraAttrs={{ id: "product-card-image" }}
              />
            </a>
            <figcaption class="fizz-flow-4" id="product-details-box">
              {#if showFeaturedLabel(productLine)}
                <div
                  id={`sponsored-label-${productLine.id}`}
                  class="fizz-text-small fizz-text-subdued"
                  data-testid="featured-label"
                >
                  Sponsored
                </div>
              {/if}
              <a
                id="product-card-name"
                href={`/products/${productLine.id}`}
                class="fizz-text-large fizz-text-strong fizz-text-inherit"
                data-testid="product-card-name"
                on:click|preventDefault
              >
                {productLine.name}
              </a>
              {#if displayVintages}
                <div id="list-product-card-vintages" class="default-cursor">
                  <ProductCardVintages {vintages} />
                </div>
              {/if}
              {#if formatCategoryInfo()}
                <p class="fizz-text-subdued default-cursor">
                  {formatCategoryInfo()}
                </p>
              {/if}
              {#if formatGeography(productLine)}
                <p class="fizz-text-subdued default-cursor">
                  {formatGeography(productLine)}
                </p>
              {/if}

              {#if !hideAllDistributorInfo}
                <div class="fizz-max-content" class:fizz-flex-row-4={!mobileMode}>
                  <ProductCardDistributors
                    {productLine}
                    {stateParams}
                    clickable={true}
                    extraTooltipClass="fizz-tooltip-top"
                    {distributorOverride}
                    clickableTextClass={"fizz-text-inherit"}
                    targetObjectType={"product_card_distributor_link"}
                  />
                  <ProductCardSalesDivisions
                    {productLine}
                    {stateParams}
                    alignHorizontally={!mobileMode}
                  />
                </div>
              {/if}
            </figcaption>
          </figure>
        </div>

        {#if !hideAllDistributorInfo}
          <!-- Price range, item count, and accordion button -->
          <div class:fizz-surface-gray={mobileMode && accordionOpen}>
            <div
              id="product-list-clickable-card-{productLine.id}"
              class="fizz-flex-row fizz-inset-squish-16"
              class:fizz-space-between={mobileMode}
              class:fizz-gap-4={!hasAccordion}
            >
              {#if loadingPrices}
                <div style="display:inline-block">
                  <Skeleton animate={true} height={60} width={200}>
                    <rect width="200" height="15" x="0" y="10" rx="12" ry="12" />
                    <rect width="80" height="15" x={mobileMode ? 0 : 120} y="35" rx="12" ry="12" />
                  </Skeleton>
                </div>
              {:else}
                <div class="fizz-flex-vertical">
                  <div
                    class="fizz-flex-row-4"
                    class:fizz-stack-8={!mobileMode && !showBadges}
                    class:fizz-stack-4={mobileMode}
                    class:fizz-justify-end={!mobileMode}
                  >
                    {#if hasDeal}
                      <div class="deal-marker" data-testid="deal-tag">
                        <Icon
                          className="fizz-icon-16"
                          type="DealCircled"
                          height="16"
                          width="16"
                          color="#055C3C"
                        />
                      </div>
                    {/if}

                    <ProductCardPrices
                      {hidePrice}
                      {productLine}
                      {inventoryPrices}
                      bind:trackingEntity={productCardPricesEntity}
                      extraClasses="fizz-text-strong"
                    />
                  </div>
                  {#if showBadges}
                    <div
                      data-testId={`badge-container-${productLine.id}`}
                      class="fizz-stack-4 fizz-text-deal"
                      class:fizz-flex-row-4={!mobileMode}
                      class:fizz-justify-end={!mobileMode}
                    >
                      {#if showItemsInStock}
                        <ProductCardBadge
                          iconType={getIconType(productLineStockStatus)}
                          badgeText={getBadgeText(productLine, productLineStockStatus)}
                          textColor={getTextColor(productLineStockStatus)}
                          testIdSuffix={"in-stock"}
                          subdued={false}
                          surfaceClass={mobileMode && accordionOpen
                            ? "fizz-surface-gray"
                            : "fizz-surface"}
                        />
                      {/if}
                      {#if showItemsInStock && showFuturePricesAvailable && !mobileMode}
                        <span class="fizz-text-subdued fizz-text-small">·</span>
                      {/if}
                      {#if showFuturePricesAvailable}
                        <ProductCardBadge
                          iconType="DoubleArrow"
                          badgeText="Next month's price available"
                          testIdSuffix="future-prices-available"
                          surfaceClass={mobileMode && accordionOpen
                            ? "fizz-surface-gray"
                            : "fizz-surface"}
                        />
                      {/if}
                    </div>
                  {/if}

                  {#if productInventoryCount && hasAccordion}
                    <div class="fizz-flex-row" class:fizz-justify-end={!mobileMode}>
                      <p
                        class="fizz-text-subdued fizz-text-small"
                        data-testid="list-card-item-count"
                      >
                        {pluralize("option", productInventoryCount, true)}
                      </p>
                    </div>
                  {/if}
                </div>
              {/if}
              {#if hasAccordion}
                <button
                  on:mousedown|preventDefault={() => onClickAccordion(trackingContext.entities)}
                  class="fizz-icon-button"
                  data-testid={accordionButtonTestId}
                >
                  <Icon type={accordionIcon} height="12" width="12" fill="black" />
                </button>
              {:else}
                <div class="fizz-inset-horizontal-16" />
              {/if}
            </div>
          </div>
        {/if}
      </div>
      {#if accordionOpen}
        <div>
          <SkuItems
            bind:productInventoryCount
            bind:productInventory
            on:openDrawer
            {listViewAccordionStatesKey}
            {productLine}
            {Cart}
            {stateParams}
          />
        </div>
      {/if}
    </div>
  </TrackingContext>
{/await}
